import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { HolidayType } from "../types/holidays.enum";
import { getSaturdaysAndSundaysOfNext15Days } from "../utils/utils";
import { BillingType } from "../types/billingType";
import { getBusinessDetailsSchema } from "../schema/businessDetailsSchema";
import { RootState } from "./store";

export type InsuranceType = {
  offerId: string;
  price: number;
  optionIdentifier?: string;
  type: "EXTERNAL" | "INSURANCE" | "TRANSPORT";
};

export { BillingType };

export type BusinessAddress = {
  street: string;
  street2: string;
  postCode: string;
  city: string;
  region: string;
  country: string;
};

const emptyBusinessAddress: BusinessAddress = {
  street: "",
  street2: "",
  postCode: "",
  city: "",
  region: "",
  country: "",
};

type InsuranceState = {
  insurance: InsuranceType[];
  storageData: any;
  holidays: HolidayType[];
  reservationFinalTotal: number;
  reservationStartDate: string;
  isResumingReservation: boolean;         // Refactor
  isUnpaidReservation: boolean;  // Refactor
  billingType: BillingType;
  businessName: string;
  businessNumber: string;
  businessBillingEmail: string;
  businessPhoneNumber: string;
  businessBillingAddress: BusinessAddress;
  businessDeliveryAddress: BusinessAddress;
  businessDeliverySameAsBilling: boolean;
  businessDetailsConfirmed: boolean;
};

const initialState: InsuranceState = {
  insurance: [],
  storageData: {},
  holidays: [], // should contain current year holidays
  reservationFinalTotal: 0,
  reservationStartDate: "",
  isResumingReservation: false,        // Refactor
  isUnpaidReservation: false, // refactor
  billingType: BillingType.INDIVIDUAL,
  businessName: "",
  businessNumber: "",
  businessBillingEmail: "",
  businessPhoneNumber: "",
  businessBillingAddress: emptyBusinessAddress,
  businessDeliveryAddress: emptyBusinessAddress,
  businessDeliverySameAsBilling: true,
  businessDetailsConfirmed: false,
};

export const isBusinessFieldsValid = (fields: {
  businessName: string;
  businessNumber: string;
  businessBillingEmail: string;
  businessPhoneNumber: string;
}): boolean =>
  getBusinessDetailsSchema().isValidSync({
    businessName: fields.businessName,
    businessNumber: fields.businessNumber,
    billingEmail: fields.businessBillingEmail,
    phoneNumber: fields.businessPhoneNumber,
  });

export const checkoutStoreSlice = createSlice({
  name: "checkoutStore",
  initialState,
  reducers: {
    setInsurances: (state, action: PayloadAction<InsuranceType[]>) => {
      state.insurance = action.payload;
    },
    setStorageData: (state, action: PayloadAction<any>) => {
      state.storageData = action.payload;
    },
    setHolidays: (state, action: PayloadAction<HolidayType[]>) => {
      state.holidays = action.payload;
    },
    // New actions for reservation data
    setReservationFinalTotal: (state, action: PayloadAction<number>) => {
      state.reservationFinalTotal = action.payload;
    },
    setReservationStartDate: (state, action: PayloadAction<string>) => {
      state.reservationStartDate = action.payload;
    },
    // NEW REDUCERS
    setIsResumingReservation: (state, action: PayloadAction<boolean>) => {
      state.isResumingReservation = action.payload;
    },
    setIsUnpaidReservation: (state, action: PayloadAction<boolean>) => {
      state.isUnpaidReservation = action.payload;
    },
    setBillingType: (state, action: PayloadAction<BillingType>) => {
      state.billingType = action.payload;
      state.businessDetailsConfirmed = false;
    },
    setBusinessName: (state, action: PayloadAction<string>) => {
      state.businessName = action.payload;
      state.businessDetailsConfirmed = false;
    },
    setBusinessNumber: (state, action: PayloadAction<string>) => {
      state.businessNumber = action.payload;
      state.businessDetailsConfirmed = false;
    },
    setBusinessBillingEmail: (state, action: PayloadAction<string>) => {
      state.businessBillingEmail = action.payload;
      state.businessDetailsConfirmed = false;
    },
    setBusinessPhoneNumber: (state, action: PayloadAction<string>) => {
      state.businessPhoneNumber = action.payload;
      state.businessDetailsConfirmed = false;
    },
    setBusinessBillingAddress: (state, action: PayloadAction<Partial<BusinessAddress>>) => {
      state.businessBillingAddress = { ...state.businessBillingAddress, ...action.payload };
      state.businessDetailsConfirmed = false;
    },
    setBusinessDeliveryAddress: (state, action: PayloadAction<Partial<BusinessAddress>>) => {
      state.businessDeliveryAddress = { ...state.businessDeliveryAddress, ...action.payload };
      state.businessDetailsConfirmed = false;
    },
    setBusinessDeliverySameAsBilling: (state, action: PayloadAction<boolean>) => {
      state.businessDeliverySameAsBilling = action.payload;
      state.businessDetailsConfirmed = false;
    },
    setBusinessDetailsConfirmed: (state, action: PayloadAction<boolean>) => {
      state.businessDetailsConfirmed = action.payload;
    },
  },
});

export const { setInsurances, setHolidays, setStorageData, setReservationFinalTotal, setReservationStartDate,
   setIsResumingReservation, setIsUnpaidReservation, setBillingType, setBusinessName, setBusinessNumber,
   setBusinessBillingEmail, setBusinessPhoneNumber, setBusinessBillingAddress, setBusinessDeliveryAddress,
   setBusinessDeliverySameAsBilling, setBusinessDetailsConfirmed,
   } = checkoutStoreSlice.actions;
export const selectInsurances = (state: any) => state.checkoutStore.insurance;
export const selectHolidays = (state: any) => state.checkoutStore.holidays;
export const selectStorageData = (state: any) => state.checkoutStore.storageData;
export const selectStorageLabel = (state: any) => state.checkoutStore.storageData?.storageUnit?.label || " -";

export const selectReservationFinalTotal = (state: any) => state.checkoutStore.reservationFinalTotal;
export const selectReservationStartDate = (state: any) => state.checkoutStore.reservationStartDate;

// select insurance type "EXTERNAL"
export const selectExternalInsurance = (state: any) =>
  state.checkoutStore.insurance.filter((insurance: InsuranceType) => insurance.type === "EXTERNAL");

export const selectIsResumingReservation = (state: RootState) => state.checkoutStore.isResumingReservation;
export const selectIsUnpaidReservation = (state: RootState) => state.checkoutStore.isUnpaidReservation;

export const selectBillingType = (state: RootState) => state.checkoutStore.billingType;
export const selectBusinessName = (state: RootState) => state.checkoutStore.businessName;
export const selectBusinessNumber = (state: RootState) => state.checkoutStore.businessNumber;
export const selectBusinessBillingEmail = (state: RootState) => state.checkoutStore.businessBillingEmail;
export const selectBusinessPhoneNumber = (state: RootState) => state.checkoutStore.businessPhoneNumber;
export const selectBusinessBillingAddress = (state: RootState) => state.checkoutStore.businessBillingAddress;
export const selectBusinessDeliveryAddress = (state: RootState) => state.checkoutStore.businessDeliveryAddress;
export const selectBusinessDeliverySameAsBilling = (state: RootState) => state.checkoutStore.businessDeliverySameAsBilling;
export const selectBusinessDetailsConfirmed = (state: RootState) => state.checkoutStore.businessDetailsConfirmed;

export default checkoutStoreSlice.reducer;
